import { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { getMonthlyBills } from '@/apis/admin';
import type { MonthlyBill } from '@/types/admin.types';

import { QUERY_KEY } from '@/constants/queryKey';
import { ROUTER } from '@/constants/constants';

import Text from '@/components/common/Text';
import { Theme } from '@/styles/Theme';
import styled from '@emotion/styled';

import * as S from './style';

const convertKRTime = (utcTime: string) => {
  const date = new Date(utcTime);
  const krTime = date.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return krTime;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
  });
};

const formatAmount = (amount: number) => {
  return `${amount.toLocaleString()}원`;
};

const getStatusBadge = (status: MonthlyBill['status']) => {
  const statusMap = {
    pending: { text: '대기', color: '#666' },
    sent: { text: '발송', color: '#2196F3' },
    pending_confirmation: { text: '확인 대기', color: '#FF9800' },
    paid: { text: '결제 완료', color: '#4CAF50' },
    overdue: { text: '연체', color: '#F44336' },
    failed: { text: '실패', color: '#F44336' },
    rejected: { text: '거부', color: '#666' },
  };
  return statusMap[status] || statusMap.pending;
};

const columnhelper = createColumnHelper<MonthlyBill>();

const createColumns = (onIdClick: (id: number) => void) => [
  columnhelper.accessor('id', {
    header: '청구서 ID',
    cell: (info) => (
      <ClickableId onClick={() => onIdClick(info.getValue())}>
        #{info.getValue()}
      </ClickableId>
    ),
    size: 100,
  }),
  columnhelper.accessor('user_company', {
    header: '회사명',
    cell: (info) => info.getValue() || '-',
    size: 200,
  }),
  columnhelper.accessor('payment_method', {
    header: '결제 방식',
    cell: (info) => {
      const method = info.getValue();
      return method === 'prepaid' ? '선불' : '후불';
    },
    size: 100,
  }),
  columnhelper.accessor('billing_period_start', {
    header: '청구 기간',
    cell: (info) => {
      const start = formatDate(info.row.original.billing_period_start);
      const end = formatDate(info.row.original.billing_period_end);
      return `${start} ~ ${end}`;
    },
    size: 150,
  }),
  columnhelper.accessor('total_amount', {
    header: '금액',
    cell: (info) => formatAmount(info.getValue()),
    size: 120,
  }),
  columnhelper.accessor('status', {
    header: '상태',
    cell: (info) => {
      const badge = getStatusBadge(info.getValue());
      return (
        <S.StatusBadge color={badge.color}>
          {badge.text}
        </S.StatusBadge>
      );
    },
    size: 120,
  }),
  columnhelper.accessor('worker_count', {
    header: '작업자 수',
    cell: (info) => `${info.getValue()}명`,
    size: 80,
  }),
  columnhelper.accessor('created_at', {
    header: '생성일',
    cell: (info) => convertKRTime(info.getValue()),
    size: 150,
  }),
];

interface MonthlyBillTableProps {
  filters: {
    page?: number;
    limit?: number;
    status?: string | string[];
    payment_method?: string;
    start_date?: string;
    end_date?: string;
    billing_month?: string;
    user_id?: number;
    search?: string;
    min_amount?: number;
    max_amount?: number;
    overdue_only?: boolean;
    pending_confirmation_only?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  };
  onSortChange?: (sortBy: string | undefined, sortOrder: 'asc' | 'desc' | undefined) => void;
}

// Map column IDs to database field names
const getSortField = (columnId: string): string | null => {
  const fieldMap: Record<string, string> = {
    id: 'id',
    user_company: 'company', // Will need to sort by User.company
    payment_method: 'payment_method',
    billing_period_start: 'billing_period_start',
    total_amount: 'total_amount',
    status: 'status',
    worker_count: 'worker_count',
    created_at: 'created_at',
  };
  return fieldMap[columnId] || null;
};

const ClickableId = styled.span`
  color: ${Theme.colors.B_1};
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    opacity: 0.8;
  }
`;

const MonthlyBillTable = ({ filters, onSortChange }: MonthlyBillTableProps) => {
  const navigate = useNavigate();

  const handleRowClick = (billId: number) => {
    navigate(`${ROUTER.ADMIN.MONTHLY_BILL_DETAIL}/${billId}`);
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [QUERY_KEY.ADMIN.MONTHLY_BILLS, filters],
    queryFn: () => getMonthlyBills(filters),
  });

  const memoizedColumn = useMemo(() => createColumns(handleRowClick), [handleRowClick]);
  const memoizedData = useMemo(
    () => data?.data.bills ?? [],
    [data]
  );

  // Map sortBy to column ID for React Table
  const sortByColumnId = useMemo(() => {
    if (!filters.sortBy) return undefined;
    // Reverse mapping: find column ID from sort field
    const reverseMap: Record<string, string> = {
      id: 'id',
      company: 'user_company',
      payment_method: 'payment_method',
      billing_period_start: 'billing_period_start',
      total_amount: 'total_amount',
      status: 'status',
      worker_count: 'worker_count',
      created_at: 'created_at',
    };
    return reverseMap[filters.sortBy] || undefined;
  }, [filters.sortBy]);

  const { getHeaderGroups, getRowModel, getCenterTotalSize } = useReactTable({
    columns: memoizedColumn,
    data: memoizedData,
    sortDescFirst: true,
    enableSorting: true,
    enableMultiSort: false, // Only single column sort
    enableSortingRemoval: true,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    getCoreRowModel: getCoreRowModel(),
    // Disable client-side sorting - sorting is done on backend
    manualSorting: true,
    state: {
      sorting: sortByColumnId ? [{
        id: sortByColumnId,
        desc: filters.sortOrder === 'desc',
      }] : [],
    },
    onSortingChange: (updater) => {
      if (!onSortChange) return;
      
      const newSorting = typeof updater === 'function' 
        ? updater(sortByColumnId ? [{ id: sortByColumnId, desc: filters.sortOrder === 'desc' }] : [])
        : updater;
      
      if (newSorting.length === 0) {
        onSortChange(undefined, undefined);
      } else {
        const sort = newSorting[0];
        const sortField = getSortField(sort.id);
        if (sortField) {
          onSortChange(sortField, sort.desc ? 'desc' : 'asc');
        }
      }
    },
  });

  if (isLoading) {
    return <Text>로딩 중...</Text>;
  }

  if (isError) {
    return (
      <Text color="red">
        오류가 발생했습니다: {error instanceof Error ? error.message : '알 수 없는 오류'}
      </Text>
    );
  }

  // Calculate page info
  const total = data?.data?.total || 0;
  const currentPage = filters.page || 1;
  const currentLimit = filters.limit || 10;
  const start = total === 0 ? 0 : (currentPage - 1) * currentLimit + 1;
  const end = Math.min(currentPage * currentLimit, total);

  return (
    <S.TableContainer>
      {total > 0 && (
        <S.TableHeader>
          <Text fontSize='0.875rem' color={Theme.colors.G_2}>
            {start.toLocaleString()} - {end.toLocaleString()} / 총 {total.toLocaleString()}개
          </Text>
        </S.TableHeader>
      )}
      <S.Table>
      <thead>
        {getHeaderGroups().map((headerGroup) => (
          <S.Tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <S.Th
                key={header.id}
                colSpan={header.colSpan}
                style={{ width: header.getSize() }}
              >
                <span onClick={header.column.getToggleSortingHandler()}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </span>
                {header.column.getIsSorted()
                  ? header.column.getIsSorted() === 'desc'
                    ? ' 🔽'
                    : ' 🔼'
                  : ''}
                <S.ResizerLine
                  onDoubleClick={() => header.column.resetSize()}
                  onMouseDown={header.getResizeHandler()}
                  onTouchStart={header.getResizeHandler()}
                  isResizing={header.column.getIsResizing()}
                />
              </S.Th>
            ))}
          </S.Tr>
        ))}
      </thead>
      <tbody>
        {getRowModel().rows.length === 0 ? (
          <S.Tr>
            <S.Td colSpan={memoizedColumn.length} style={{ textAlign: 'center' }}>
              데이터가 없습니다.
            </S.Td>
          </S.Tr>
        ) : (
          getRowModel().rows.map((row) => (
            <S.Tr
              key={row.id}
              onClick={() => handleRowClick(row.original.id)}
              style={{ cursor: 'pointer' }}
            >
              {row.getVisibleCells().map((cell) => (
                <S.Td key={cell.id} style={{ width: cell.column.getSize() }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </S.Td>
              ))}
            </S.Tr>
          ))
        )}
      </tbody>
    </S.Table>
    </S.TableContainer>
  );
};

export default MonthlyBillTable;

